import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';

import ReactFlow, { Background, Controls, Edge, MarkerType, Node, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import CubeRoutedEdge from './components/CubeRoutedEdge';
import CubeUMLNode from './components/CubeUMLNode';
import { getLayoutedElements, layoutOptions } from './utils/elkLayout';
import { routeDiagramEdges } from './utils/diagramRouting';

import {
  GridRowModesModel,
  GridFilterModel,
  useGridApiRef,
  GridSortModel,
  GridColumns,
  GridActionsCellItem,
} from '@mui/x-data-grid-pro';
import { useSubjectUsedDropdown } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { PaginationAndSortingParams } from 'types/api/Common/PaginationTypes';
import { CustomDataTable } from 'components/DatatableComponent';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { SettingPanel } from 'pages/SystemTenant/AROKMS/KTree/components/KTreeTablePanel';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';

import { DASHBOARD_VIEW_MODE } from 'constant/DashboardTypeConstant';
import { SUBJECT_TYPE } from 'constant/DataInputConstant';
import { useGetAllCubeDefinitionList, useGetCubeDefinitionDiagramAll } from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { CubeDefinitionItem } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import { PATH_CONSTANT } from 'constant/PathConstant';

interface MemberListFilter extends PaginationAndSortingParams {
  subjectId?: string | number;
}

const panelStyle = {
  transform: 'translate3d(704px, 140.667px, 0px) !important',

  '& .MuiDataGrid-panelFooter': {
    display: 'none',
  },
};
const COLUMN_WIDTH = 150;
const diagramFilterChipStyle = {
  fontSize: '11px',
  height: 24,
  bgcolor: '#EDF2FF',
  color: '#3B4797',
  fontWeight: 600,
};

const diagramFilterAutocompleteStyle = {
  minWidth: 320,
  maxWidth: 460,
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: 40,
    padding: '2px 6px',
    backgroundColor: '#FFFFFF',
  },
};
const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

const nodeTypes = { cubeUML: CubeUMLNode };
const edgeTypes = { smoothstep: CubeRoutedEdge };

export default function CubeDefinitionListPage() {
  const { t } = useTranslation();
  const { dashboardViewMode } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const [muiDataTableKey] = useState<number>(0);
  const [rowData, setRowData] = useState<CubeDefinitionItem[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [filterButtonElement, setFilterButtonElement] = useState<HTMLButtonElement | null>(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState<AutoCompleteItem>({ label: '', value: '' });
  const [filter, setFilter] = useState<MemberListFilter>({
    page: 0,
    size: 20,
    filterValue: '',
    sortType: 'asc',
    filterOperator: 'contains',
  });
  const [isDiagramDialogOpen, setIsDiagramDialogOpen] = useState(false);
  const [diagramNodes, setDiagramNodes] = useState<Node[]>([]);
  const [diagramEdges, setDiagramEdges] = useState<Edge[]>([]);
  const [isLayouting, setIsLayouting] = useState(false);
  const [selectedDiagramAttributes, setSelectedDiagramAttributes] = useState<AutoCompleteItem[]>([]);
  const [selectedDiagramDataFlowTypes, setSelectedDiagramDataFlowTypes] = useState<AutoCompleteItem[]>([]);

  const selectedDiagramAttributeIds = useMemo(
    () => selectedDiagramAttributes.map((item) => item.value.toString()),
    [selectedDiagramAttributes]
  );
  const selectedDiagramDataFlowTypeValues = useMemo(
    () => selectedDiagramDataFlowTypes.map((item) => item.value.toString()),
    [selectedDiagramDataFlowTypes]
  );

  // Service call
  const { data: subjectDropdownData, isLoading: isDropdownLoading } = useSubjectUsedDropdown({
    enabled: true,
    subjectType: SUBJECT_TYPE.TABLE,
  });
  const { isLoading, data: cubeDefinitionListData } = useGetAllCubeDefinitionList();
  const { isFetching: isDiagramLoading, data: diagramData } = useGetCubeDefinitionDiagramAll({
    layout: 'LR',
    attributeIds: selectedDiagramAttributeIds,
    dataFlowTypes: selectedDiagramDataFlowTypeValues,
    enabled: isDiagramDialogOpen,
  });

  useEffect(() => {
    if (!diagramData?.data) return;
    let isCancelled = false;

    const rawNodes: Node[] = (diagramData.data.nodes || []).map((node: any) => ({
      ...node,
      type: 'cubeUML',
    }));

    const rawEdges: Edge[] = (diagramData.data.edges || []).map((edge: any) => ({
      ...edge,
      type: 'smoothstep',
      label: undefined,
      animated: false,
      interactionWidth: 20,
      style: { stroke: '#94a3b8', strokeWidth: 1.15 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8', width: 16, height: 16 },
    }));

    if (rawNodes.length === 0 && rawEdges.length === 0) {
      setDiagramNodes([]);
      setDiagramEdges([]);
      setIsLayouting(false);
      return;
    }

    const opts = {
      ...layoutOptions,
      direction: (diagramData.data.layout === 'TB' ? 'DOWN' : 'RIGHT') as 'DOWN' | 'RIGHT',
    };

    setIsLayouting(true);
    getLayoutedElements(rawNodes, rawEdges, opts).then((result) => {
      if (isCancelled) return;

      if (result) {
        setDiagramNodes(result.nodes);
        setDiagramEdges(routeDiagramEdges(result.nodes, result.edges, opts.direction));
      } else {
        setDiagramNodes(rawNodes);
        setDiagramEdges(routeDiagramEdges(rawNodes, rawEdges, opts.direction));
      }

      setIsLayouting(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [diagramData?.data]);

  const isBuilderViewOnly = dashboardViewMode === DASHBOARD_VIEW_MODE.VIEW_ONLY;

  const subjectFilterOptions = useMemo(
    () => Array.from(new Set(rowData.map((item) => item.subjectDisplayName).filter(Boolean))),
    [rowData]
  );

  const columns = useMemo<GridColumns>(() => {
    const cubeCols: GridColumns = [
      {
        field: 'cubeCode',
        headerName: 'Code',
        width: 80,
        editable: true,
        filterable: true,
        sortable: true,
      },
      {
        field: 'cubeName',
        headerName: 'Cube Name',
        width: COLUMN_WIDTH,
        editable: true,
        filterable: true,
        sortable: true,
      },
      {
        field: 'subjectDisplayName',
        headerName: 'Subject',
        width: COLUMN_WIDTH,
        editable: true,
        filterable: true,
        sortable: true,
        type: 'singleSelect',
        valueOptions: subjectFilterOptions,
      },
      {
        field: 'member',
        headerName: 'KTree (M)',
        width: COLUMN_WIDTH,
        editable: true,
        filterable: true,
        sortable: true,
      },
      {
        field: 'subjectX',
        headerName: 'KTree (X)',
        width: COLUMN_WIDTH,
        editable: true,
        filterable: true,
        sortable: true,
      },
      {
        field: 'timePeriodSetCode',
        headerName: 'Time Period Set (T)',
        width: COLUMN_WIDTH,
        editable: true,
        filterable: true,
        sortable: true,
      },
      {
        field: 'timePeriodSetName',
        headerName: 'Period Set Name',
        width: COLUMN_WIDTH,
        editable: true,
        filterable: true,
        sortable: true,
      },
      {
        field: 'layerSetCode',
        headerName: 'Layer Set (T)',
        width: COLUMN_WIDTH,
        editable: true,
        filterable: true,
        sortable: true,
      },
      {
        field: 'layerSetName',
        headerName: 'Layer Set Name',
        width: COLUMN_WIDTH,
        editable: true,
        filterable: true,
        sortable: true,
      },
      {
        field: 'cubeDescription',
        headerName: 'Description',
        width: 450,
        editable: true,
        filterable: true,
        sortable: true,
      },
    ];

    if (isBuilderViewOnly) return cubeCols;
    return [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Edit',
        width: 30,
        cellClassName: 'actions',

        getActions: ({ id }) => {
          return [
            // @ts-ignore
            <GridActionsCellItem
              icon={<EditIcon />}
              label='Edit'
              onClick={() => {
                if (selectedSubjectId?.value) {
                  navigate(
                    PATH_CONSTANT.SYSTEM.CUBE.CUBE_DEFINITION_EDIT.replace(
                      ':subjectId',
                      selectedSubjectId?.value as string
                    ).replace(':cubeId', id as string)
                  );
                }
              }}
            />,
          ];
        },
      },
      ...cubeCols,
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubjectId?.value, isBuilderViewOnly, subjectFilterOptions]);

  const subjectDropdownOptions = useMemo(() => {
    if (subjectDropdownData?.data) {
      setSelectedSubjectId(subjectDropdownData.data[0]);
      return subjectDropdownData.data;
    }
    return [];
  }, [subjectDropdownData]);

  useEffect(() => {
    if (cubeDefinitionListData?.data) {
      setRowData(cubeDefinitionListData.data);
    }
  }, [cubeDefinitionListData?.data]);

  const handleChangePage = (newPage: number) => {
    setFilter({ ...filter, page: newPage });
  };
  const handleChangePageSize = (newPageSize: number) => {
    setFilter({ ...filter, size: newPageSize });
  };

  const handleOnFilter = (params: GridFilterModel) => {
    let filterObj = {};
    if (params.items.length) {
      filterObj = {
        filterValue: params.items[0].value,
        filterOperator: params.items[0].operatorValue,
      };
    } else {
      filterObj = {
        filterValue: '',
        filterOperator: 'contains',
      };
    }
    setFilter({
      ...filter,
      ...filterObj,
    });
  };

  const handleOnSortModelChange = (model: GridSortModel) => {
    if (model.length === 0) {
      setFilter({ ...filter, sortType: 'asc' });
      return;
    }
    setFilter({ ...filter, sortType: model[0].sort });
  };

  const handleOnAddButtonClick = () => {
    navigate(`/cube/definition/${selectedSubjectId.value}/create`);
  };

  const handleOpenDiagram = () => {
    setIsDiagramDialogOpen(true);
  };

  const handleCloseDiagram = () => {
    setIsDiagramDialogOpen(false);
  };

  const handleOnDiagramAttributeChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newValue: AutoCompleteItem[]
  ) => {
    setSelectedDiagramAttributes(newValue);
  };

  const handleClearDiagramAttributeFilter = () => {
    setSelectedDiagramAttributes([]);
  };
  const handleOnDiagramDataFlowTypeChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newValue: AutoCompleteItem[]
  ) => {
    setSelectedDiagramDataFlowTypes(newValue);
  };

  const handleClearDiagramDataFlowTypeFilter = () => {
    setSelectedDiagramDataFlowTypes([]);
  };

  const diagramAttributeOptions = diagramData?.data?.attributeDropdownItems || [];
  const diagramDataFlowTypeOptions = diagramData?.data?.dataFlowTypeDropdownItems || [];

  return (
    <MainLayoutComponent pageTitle='Cube Definition' breadcrumbs={[t('SIDEBAR_MENU.DASHBOARD'), 'ARO Cube']}>
      <CustomDataTable
        key={muiDataTableKey}
        apiRef={apiRef}
        initialState={{
          pinnedColumns: { right: ['action'] },
          pagination: {
            page: 0,
          },
        }}
        loading={isLoading || isDropdownLoading}
        rows={rowData}
        columns={columns}
        rowCount={rowData.length}
        page={filter.page}
        pageSize={filter.size}
        getRowId={(row) => row.id}
        rowModesModel={rowModesModel}
        checkboxSelection={false}
        disableSelectionOnClick
        rowsPerPageOptions={[5, 10, 20]}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        experimentalFeatures={{ newEditingApi: true }}
        onFilterModelChange={handleOnFilter}
        onSortModelChange={handleOnSortModelChange}
        onPageChange={handleChangePage}
        paginationMode='client'
        sortingMode='client'
        filterMode='client'
        onPageSizeChange={handleChangePageSize}
        autoHeight
        components={{
          Toolbar: SettingPanel,
        }}
        componentsProps={{
          panel: {
            sx: panelStyle,
            anchorEl: filterButtonElement,
          },
          toolbar: {
            setRows: setRowData,
            setRowModesModel,
            setFilterButtonElement: setFilterButtonElement,
            disabled: isLoading || isBuilderViewOnly,
            onAddButtonClick: handleOnAddButtonClick,
            hideSubjectDropdown: true,
            buttonComponent: (
              <Stack direction="row" spacing={2}>
                <Button
                  disabled={isLoading || isBuilderViewOnly || subjectDropdownOptions.length === 0}
                  variant='main-table-panel'
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                  onClick={handleOnAddButtonClick}
                >
                  Add New Cube
                </Button>
                <Button
                  disabled={isLoading}
                  variant='secondary-table-panel'
                  onClick={handleOpenDiagram}
                >
                  View Diagram
                </Button>
              </Stack>
            ),
            config: {
              fieldFocusOnInsert: '',
              insertButtonText: 'Add New Cube',
            },
          },
        }}
      />

      <Dialog
        open={isDiagramDialogOpen}
        onClose={handleCloseDiagram}
        maxWidth='xl'
        fullWidth
      >
        <Stack justifyContent='space-between' direction='row' spacing={2} alignItems='center' sx={{ px: 3, py: 2 }}>
          <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
            All Cube Dependency Chains
          </Typography>
          <IconButton onClick={handleCloseDiagram} component='label'>
            <HighlightOffRoundedIcon sx={closeIconStyle} />
          </IconButton>
        </Stack>
        <Divider />
        <DialogContent sx={{ pt: 0 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent='space-between'
            spacing={2}
            sx={{ mt: 2, px: 2, py: 1.5, bgcolor: '#FBFBFB', border: '1px solid #E3EBF6', borderRadius: 1 }}
          >
            <Stack spacing={0.75}>
              <Typography variant='body2' fontWeight='bold' color='black'>
                Attribute Filter
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Leave it blank to show the full dependency chain.
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap',
                width: '100%',
                justifyContent: { xs: 'stretch', md: 'flex-end' },
              }}
            >
              <Box sx={{ minWidth: 320, maxWidth: 460, flex: '1 1 320px' }}>
                <Autocomplete
                  multiple
                  size='small'
                  disableCloseOnSelect
                  value={selectedDiagramAttributes}
                  options={diagramAttributeOptions}
                  loading={isDiagramLoading && diagramAttributeOptions.length === 0}
                  onChange={handleOnDiagramAttributeChange}
                  getOptionLabel={(option: AutoCompleteItem) => option.label}
                  isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) => option.value === value.value}
                  noOptionsText={isDiagramLoading ? 'Loading attributes...' : 'No attributes available'}
                  sx={diagramFilterAutocompleteStyle}
                  renderTags={(value) =>
                    value.length > 0 ? (
                      <Chip
                        label={`${value.length} attribute${value.length > 1 ? 's' : ''} selected`}
                        size='small'
                        onDelete={handleClearDiagramAttributeFilter}
                        sx={diagramFilterChipStyle}
                      />
                    ) : null
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        size='small'
                        sx={{
                          mr: 1,
                          color: '#98A2AE',
                          '&.Mui-checked': {
                            color: '#42BB93',
                          },
                        }}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label='Attributes' placeholder='All attributes' />}
                />
              </Box>
              <Box sx={{ minWidth: 280, maxWidth: 360, flex: '1 1 280px' }}>
                <Autocomplete
                  multiple
                  size='small'
                  disableCloseOnSelect
                  value={selectedDiagramDataFlowTypes}
                  options={diagramDataFlowTypeOptions}
                  loading={isDiagramLoading && diagramDataFlowTypeOptions.length === 0}
                  onChange={handleOnDiagramDataFlowTypeChange}
                  getOptionLabel={(option: AutoCompleteItem) => option.label}
                  isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) => option.value === value.value}
                  noOptionsText={isDiagramLoading ? 'Loading data flow types...' : 'No data flow types available'}
                  sx={diagramFilterAutocompleteStyle}
                  renderTags={(value) =>
                    value.length > 0 ? (
                      <Chip
                        label={`${value.length} flow type${value.length > 1 ? 's' : ''} selected`}
                        size='small'
                        onDelete={handleClearDiagramDataFlowTypeFilter}
                        sx={diagramFilterChipStyle}
                      />
                    ) : null
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        size='small'
                        sx={{
                          mr: 1,
                          color: '#98A2AE',
                          '&.Mui-checked': {
                            color: '#42BB93',
                          },
                        }}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label='Data Flow Type' placeholder='All data flow types' />
                  )}
                />
              </Box>
            </Box>
          </Stack>
          <Box sx={{ width: '100%', height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center', pt: 2 }}>
            {isDiagramLoading || isLayouting ? (
              <CircularProgress />
            ) : diagramNodes.length > 0 || diagramEdges.length > 0 ? (
              <Box sx={{ width: '100%', height: '100%' }}>
                <ReactFlowProvider>
                  <ReactFlow
                    nodes={diagramNodes}
                    edges={diagramEdges}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.24, includeHiddenNodes: false }}
                    minZoom={0.1}
                    maxZoom={1.5}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    defaultEdgeOptions={{
                      type: 'smoothstep',
                      animated: false,
                      interactionWidth: 20,
                    }}
                  >
                    <Background gap={16} size={1} color='#e2e8f0' />
                    <Controls />
                  </ReactFlow>
                </ReactFlowProvider>
              </Box>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                {selectedDiagramAttributes.length > 0 || selectedDiagramDataFlowTypes.length > 0
                  ? 'No diagram data available for the selected filters.'
                  : 'No diagram data available.'}
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </MainLayoutComponent>
  );
}
